import React, { useEffect } from 'react'
import { Button, Card, Form, Input, PageHeader, Space, message } from 'antd'
import { useMutation, useQuery } from 'react-query'
import { getUserDetails, updateUser } from '../../network/Users'
import { getWechatBindUrl } from '../../network/Wechat'
import { useAuth } from '../../hooks/useAuth'
import Loader from '../../components/Loader'
import Error from '../../components/Error'

const EditProfile = ({ history }) => {
  const { user, refreshStore } = useAuth()
  const [form] = Form.useForm()

  const userDetails = useQuery(
    ['getUserDetails', { id: user?.id }],
    getUserDetails,
    {
      enabled: !!user?.id,
      refetchOnWindowFocus: false,
    }
  )

  useEffect(() => {
    if (!userDetails.data) return
    form.setFieldsValue({
      name: userDetails.data.name || '',
      password: '',
      confirmPassword: '',
    })
  }, [userDetails.data, form])

  const updateProfile = useMutation(
    (values) => {
      const payload = {
        ...userDetails.data,
        name: values.name,
      }
      if (values.password) payload.password = values.password
      return updateUser(payload, user.id)
    },
    {
      onSuccess: () => {
        message.success('Profile updated')
        refreshStore()
      },
      onError: () => {
        message.error('Failed to update profile')
      },
    }
  )

  const bindWechat = useMutation(getWechatBindUrl, {
    onSuccess: (response) => {
      const loginUrl = response?.loginUrl || response?.data?.loginUrl

      if (!loginUrl) {
        message.error('Unable to generate WeChat bind URL')
        return
      }

      window.open(loginUrl, '_blank', 'noopener,noreferrer')
      message.success('WeChat bind URL generated')
    },
    onError: () => {
      message.error('Failed to generate WeChat bind URL')
    },
  })

  if (userDetails.isLoading) return <Loader />
  if (userDetails.isError) return <Error retry={() => userDetails.refetch()} />

  return (
    <>
      <PageHeader
        className="site-page-header"
        title="My profile"
        onBack={() => history.goBack()}
        style={{ backgroundColor: '#fff' }}
      />
      <div style={{ padding: 24 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card style={{ maxWidth: 600 }}>
          <Form
            layout="vertical"
            form={form}
            onFinish={(values) => updateProfile.mutate(values)}
          >
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please enter your name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="password"
              label="New password"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value || value.length >= 6) return Promise.resolve()
                    return Promise.reject(
                      new Error('Password must be at least 6 characters')
                    )
                  },
                },
              ]}
            >
              <Input.Password placeholder="Leave empty to keep current password" />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm new password"
              dependencies={['password']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!getFieldValue('password') && !value) {
                      return Promise.resolve()
                    }
                    if (getFieldValue('password') === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error('Passwords do not match'))
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Space>
                <Button
                  style={{ display: 'none' }}
                  type="default"
                  onClick={() => bindWechat.mutate()}
                  loading={bindWechat.isLoading}
                >
                  Bind WeChat
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={updateProfile.isLoading}
                >
                  Save
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
        </Space>
      </div>
    </>
  )
}

export default EditProfile
